import type { JSX } from 'react';
import CategoryIcon from '@mui/icons-material/Category';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HistoryIcon from '@mui/icons-material/History';
import HomeIcon from '@mui/icons-material/Home';
import PublicIcon from '@mui/icons-material/Public';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

import { useAuth } from '../auth/AuthProvider';
import { AppShell, type ShellNavGroup } from '../layout/AppShell';
import { WorkspaceSwitcher } from '../layout/WorkspaceSwitcher';
import { canAccessPlatformModule } from './platform-access';

const navIconProps = { fontSize: 'small' } as const;

export function PlatformLayout(): JSX.Element {
  const auth = useAuth();
  const canAccessSystemOverview = canAccessPlatformModule(auth.platformPermissions, 'system');
  const canAccessBillingCatalog = canAccessPlatformModule(auth.platformPermissions, 'billingCatalog');
  const canAccessMaterialsCatalog = canAccessPlatformModule(
    auth.platformPermissions,
    'materialsCatalog',
  );
  const canAccessPlatformGrants = canAccessPlatformModule(
    auth.platformPermissions,
    'platformGrants',
  );
  const canAccessAuditLogs = canAccessPlatformModule(auth.platformPermissions, 'auditLogs');
  const canAccessRegulatoryDirectory = canAccessPlatformModule(
    auth.platformPermissions,
    'regulatoryDirectory',
  );

  const navGroups: ShellNavGroup[] = [
    {
      id: 'workspace',
      label: 'Workspace',
      items: [
        {
          to: '/platform',
          label: 'Home',
          icon: <HomeIcon {...navIconProps} />,
          end: true,
        },
      ],
    },
    {
      id: 'system',
      label: 'System',
      items: [
        ...(canAccessSystemOverview
          ? [
              {
                to: '/platform/system',
                label: 'Overview',
                icon: <DashboardIcon {...navIconProps} />,
                inactiveWhen: [
                  '/platform/system/audit-logs',
                  '/platform/system/billing-catalog',
                  '/platform/system/materials-catalog',
                  '/platform/system/platform-grants',
                ],
              },
            ]
          : []),
        ...(canAccessBillingCatalog
          ? [
              {
                to: '/platform/system/billing-catalog/plans',
                label: 'Billing Catalog',
                icon: <ReceiptLongIcon {...navIconProps} />,
                activeWhen: ['/platform/system/billing-catalog'],
              },
            ]
          : []),
        ...(canAccessMaterialsCatalog
          ? [
              {
                to: '/platform/system/materials-catalog',
                label: 'Materials Catalog',
                icon: <CategoryIcon {...navIconProps} />,
                activeWhen: ['/platform/system/materials-catalog'],
              },
            ]
          : []),
        ...(canAccessPlatformGrants
          ? [
              {
                to: '/platform/system/platform-grants',
                label: 'Platform Grants',
                icon: <VpnKeyIcon {...navIconProps} />,
              },
            ]
          : []),
        ...(canAccessAuditLogs
          ? [
              {
                to: '/platform/system/audit-logs',
                label: 'Audit Logs',
                icon: <HistoryIcon {...navIconProps} />,
              },
            ]
          : []),
      ],
    },
    {
      id: 'regulatory',
      label: 'Regulatory',
      items: canAccessRegulatoryDirectory
        ? [
            {
              to: '/platform/regulatory-directory',
              label: 'Directory',
              icon: <PublicIcon {...navIconProps} />,
              inactiveWhen: ['/platform/regulatory-directory/review'],
            },
            {
              to: '/platform/regulatory-directory/review',
              label: 'Review Queue',
              icon: <RateReviewIcon {...navIconProps} />,
            },
          ]
        : [],
    },
  ].filter((group) => group.items.length > 0);

  return (
    <AppShell
      workspaceLabel="Platform workspace"
      homePath="/platform"
      navGroups={navGroups}
      workspaceSwitcher={<WorkspaceSwitcher />}
      accountEmail={auth.bootstrap?.user.email ?? null}
      onLogout={() => {
        auth.logout();
      }}
    />
  );
}
